import type {
  CreateCallOffItemMutationOptions,
  CreateCallOffItemVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCallOff } from '../queryKeys';

import { createCallOffItem } from './createCallOffItem';

/**
 * Custom hook for adding a new line item to an existing call-off.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/create_calloff_item_calloffs__calloff_id__items_post
 */
export const useMutationCreateCallOffItem = (
  options?: CreateCallOffItemMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateCallOffItemVariables) =>
      createCallOffItem(variables),
    onSuccess(_data, variables) {
      // Invalidate the call-off items list
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getItems(variables.callOffId),
      });
      // Invalidate the call-off detail (since items are included in the response)
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.get(variables.callOffId),
      });
    },
    ...options,
  });
};
